import React, { memo, useRef, ReactElement } from 'react';
import {
  Box,
  Flex,
  RadioGroup,
  Radio,
  Stack,
  Collapse,
  GridItem,
  VStack,
  BoxProps,
  VisuallyHidden,
} from '@chakra-ui/react';

import { ProductTotalsItem } from '@olo-web/components/molecules/ProductTotals/ProductTotalsItem.ui';
import { TipsRadioGroup } from '@olo-web/components/atoms/TipsSection/TipsRadioGroup.ui';
import { CustomTipsInput } from '@olo-web/components/atoms/TipsSection/CustomTipsInput.ui';
import { ESpecialTipsValue, ECustomTipsMode } from '@olo-web/domain/orders/types';
import { formatPrice } from '@olo-web/domain/orders/functions/formatPrice';
import { useOrderTipsDispatch } from '@olo-web/client-state';
import { useCurrentOrderTips, useIsThirdPartyDelivery } from '@olo-web/utils/common/hooks';
import { useRouter } from 'next/router';
import { usePaymentMethodValues } from '@olo-web/domain/payments/hooks';
import { useMerchantOrderTypeDetails } from '@domain/merchants/queries/useMerchantOrderTypeDetails';
import { ContentCard } from '../ContentCard';
import { useTipAmount } from '@olo-web/domain/orders/hooks/useTipAmount';

const TipsSectionComponent = ({
  withoutTitle,
  withContentCard,
  children,
  radioGroupProps,
}: {
  withoutTitle?: boolean;
  withContentCard?: boolean;
  children?: ReactElement<any, any>;
  radioGroupProps?: BoxProps;
}) => {
  const numberInputRef = useRef<HTMLInputElement>(null);
  const orderTipsDispatch = useOrderTipsDispatch();
  const tipAmount = useTipAmount();
  const { selectedTips, customTipsMode } = useCurrentOrderTips();
  const router = useRouter();
  const { merchantId } = router.query;
  const { isCash } = usePaymentMethodValues();
  const isThirdPartyDelivery = useIsThirdPartyDelivery();
  const { data: merchantOrderTypeDetails } = useMerchantOrderTypeDetails();
  const showTip = !isCash && !!(isThirdPartyDelivery || merchantOrderTypeDetails?.tippable);

  const handleCustomTipsModeChange = (nextCustomTipsMode: ECustomTipsMode) => {
    orderTipsDispatch({
      type: 'TIPS_MODE_CHANGE',
      payload: {
        merchantId: merchantId as string,
        value: nextCustomTipsMode,
      },
    });
  };

  const Tips = (
    <Box w="100%">
      {withoutTitle ? (
        <ProductTotalsItem title="Tip" info={tipAmount} />
      ) : (
        <Flex mb="16px" justify="space-between" textStyle="p">
          <Box as="h3" textStyle="h3">
            Include a tip
          </Box>
          <Box as="span" data-testid="tip-value" textStyle="pBold" fontWeight="500">
            {formatPrice(tipAmount)}
          </Box>
        </Flex>
      )}

      <TipsRadioGroup {...radioGroupProps} />

      <Collapse
        unmountOnExit
        in={selectedTips === ESpecialTipsValue.CUSTOM}
        onTransitionEnd={() => {
          numberInputRef.current?.scrollIntoView({
            behavior: 'smooth',
          });
        }}
      >
        <Box as="fieldset" border="none" p={0} m={0} mb="8px">
          <VisuallyHidden as="legend">Custom tip type</VisuallyHidden>
          <RadioGroup
            mb="8px"
            name="custom-tips-mode"
            value={customTipsMode}
            onChange={handleCustomTipsModeChange}
          >
            <Stack direction="row">
              <Radio size="md" value={ECustomTipsMode.AMOUNT} data-testid="dollar-tip">
                <Box as="span" textStyle="p">
                  <span aria-hidden="true">$</span> Dollars
                </Box>
              </Radio>

              <Radio size="md" value={ECustomTipsMode.PERCENTAGES} data-testid="percent-tip">
                <Box as="span" textStyle="p">
                  <span aria-hidden="true">%</span> Percent
                </Box>
              </Radio>
            </Stack>
          </RadioGroup>
        </Box>

        <CustomTipsInput ref={numberInputRef} />
      </Collapse>
    </Box>
  );

  return showTip ? (
    withContentCard ? (
      <GridItem colSpan={1}>
        <ContentCard>
          <VStack mt={2}>{Tips}</VStack>
          {children || null}
        </ContentCard>
      </GridItem>
    ) : (
      <>
        {Tips}
        {children || null}
      </>
    )
  ) : (
    children || null
  );
};

export const TipsSection = memo(TipsSectionComponent);
